import React, { useCallback, useEffect, useRef, useState } from 'react';
import './CriterionWeight.css';
import {
    formatWeightValue,
    normalizeWeight,
    WEIGHT_DEFAULT,
    WEIGHT_MAX,
    WEIGHT_MIN,
} from '../../core/criteriaWeights';

const TXT_ARIA = {
    fr: 'Poids du critère',
    en: 'Criterion weight',
    de: 'Gewichtung des Kriteriums',
    es: 'Peso del criterio',
    pt: 'Peso do critério',
};

/**
 * The "×2" next to an active criterion: a label until the pointer is on it,
 * a number field once it is. Rendered inside rows that are themselves click
 * targets (a stat row toggles its criterion, a spell row selects its line), so
 * every event it handles stops there — including Enter and Space, which the
 * host rows read as "toggle me".
 */
const CriterionWeight = ({ value = WEIGHT_DEFAULT, onChange, disabled = false, lang = 'fr' }) => {
    const current = normalizeWeight(value);
    const [text, setText] = useState(() => formatWeightValue(current));
    const editingRef = useRef(false);

    useEffect(() => {
        if (editingRef.current) return;
        setText(formatWeightValue(value));
    }, [value]);

    const commit = useCallback((raw) => {
        const parsed = Number(String(raw).replace(',', '.'));
        if (!Number.isFinite(parsed)) return;
        const next = normalizeWeight(parsed);
        if (next !== current) onChange?.(next);
    }, [current, onChange]);

    // One decimal, and never above the ceiling: neither ever reaches the field,
    // so what the user reads is what gets stored. The floor is NOT applied here
    // — clamping it mid-keystroke would turn the "0" of "0.5" into "0.1" and
    // make the value untypeable. Blur handles that end.
    const handleChange = useCallback((e) => {
        let raw = e.target.value
            .replace(/[^\d.,]/g, '')
            .replace(',', '.')
            .replace(/^(\d*)(\.?)(\d?).*$/, '$1$2$3');
        if (Number(raw) > WEIGHT_MAX) raw = String(WEIGHT_MAX);
        setText(raw);
        commit(raw);
    }, [commit]);

    const handleBlur = useCallback(() => {
        editingRef.current = false;
        const parsed = Number(String(text).replace(',', '.'));
        const next = Number.isFinite(parsed) ? normalizeWeight(parsed) : current;
        setText(formatWeightValue(next));
        if (next !== current) onChange?.(next);
    }, [current, onChange, text]);

    const handleKeyDown = useCallback((e) => {
        e.stopPropagation();
        if (e.key === 'Enter' || e.key === 'Escape') e.currentTarget.blur();
    }, []);

    const stop = useCallback((e) => { e.stopPropagation(); }, []);

    const label = TXT_ARIA[lang] || TXT_ARIA.en;

    return (
        <span
            className={`criterion-weight${current !== WEIGHT_DEFAULT ? ' criterion-weight--custom' : ''}${disabled ? ' criterion-weight--disabled' : ''}`}
            onClick={stop}
            onPointerDown={stop}
        >
            <span className="criterion-weight__sign" aria-hidden="true">&times;</span>
            <input
                type="text"
                inputMode="decimal"
                className="criterion-weight__input"
                value={text}
                onChange={handleChange}
                onFocus={() => { editingRef.current = true; }}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={disabled}
                aria-label={label}
                title={`${label} (${WEIGHT_MIN}–${WEIGHT_MAX})`}
            />
        </span>
    );
};

export default CriterionWeight;
